import React, { useState, useEffect, useCallback } from 'react';
import { Box, Skeleton, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { AdminMenuBar } from '../utils/admin-menu-bar';
import { AdminMlStatus as AdminMlStatusApi } from '../../api/admin-ml';
import { AdminCfQueues as AdminCfQueuesApi } from '../../api/admin-cf-queue';
import { AdminCfR2 as AdminCfR2Api, AdminCfWorkers as AdminCfWorkersApi } from '../../api/admin-cf-usage';
import { ec2MonthToDateCost } from './aws/aws-instance';
import { PRICE_PER_OP } from './cloudflare/cf-queues';
import { r2MonthCost } from './cloudflare/cf-r2';

const REQ_PRICE = 0.3 / 1_000_000;
const CPU_MS_PRICE = 0.02 / 1_000_000;

const CELL = { whiteSpace: 'nowrap', py: 1.25, px: 4 };
const usd = (n) => `$${n.toFixed(4)}`;

function InfraDashboard() {
    const [aws, setAws] = useState({ loading: false, data: {} });
    const [queues, setQueues] = useState({ loading: false, data: [] });
    const [r2, setR2] = useState({ loading: false, data: [] });
    const [workers, setWorkers] = useState({ loading: false, data: { workers: [], pages: [] } });

    const refresh = useCallback(() => {
        AdminMlStatusApi(setAws);
        AdminCfQueuesApi(setQueues, 'month');
        AdminCfR2Api(setR2);
        AdminCfWorkersApi(setWorkers);
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const instances = Object.values(aws.data ?? {}).flatMap((e) =>
        (e.instances ?? []).map((i) => ({ ...i, template: e.template })),
    );
    const awsCost = instances.reduce((sum, i) => sum + (ec2MonthToDateCost(i.launched_at, i.instance_type) ?? 0), 0);

    const queueList = queues.data ?? [];
    const queueOps = queueList.reduce((sum, q) => sum + (q.billable_operations ?? 0), 0);
    const queueCost = queueOps * PRICE_PER_OP;

    const r2List = r2.data ?? [];
    const r2Cost = r2List.reduce((sum, b) => sum + r2MonthCost(b), 0);
    const r2Bytes = r2List.reduce((sum, b) => sum + (b.bytes ?? 0), 0);

    const workerList = workers.data?.workers ?? [];
    const pagesList = workers.data?.pages ?? [];
    const workerRequests = workerList.reduce((sum, w) => sum + (w.requests ?? 0), 0);
    const workerCpuMs = workerList.reduce((sum, w) => sum + (w.cpu_ms ?? 0), 0);
    const pagesRequests = pagesList.reduce((sum, p) => sum + (p.requests ?? 0), 0);
    const workersCost = workerRequests * REQ_PRICE + workerCpuMs * CPU_MS_PRICE + pagesRequests * REQ_PRICE;
    const workersDetail = `${(workerRequests + pagesRequests).toLocaleString()} reqs`;

    const total = awsCost + queueCost + r2Cost + workersCost;
    const loading = aws.loading || queues.loading || r2.loading || workers.loading;

    return (
        <Stack direction='column' sx={{ width: '100%', height: '100%', minHeight: 0 }}>
            <AdminMenuBar apiItems={[{ label: 'Refresh', onClick: refresh }]} />
            <Box sx={{ overflow: 'auto', flex: 1 }}>
                <Typography variant='body2' color='text.secondary' sx={{ mb: 1.5 }}>
                    Estimated cost, month-to-date.
                </Typography>
                <Table size='small' sx={{ width: '100%' }}>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={CELL}>Provider</TableCell>
                            <TableCell sx={CELL}>Component</TableCell>
                            <TableCell sx={{ ...CELL, width: '100%' }}>Detail</TableCell>
                            <TableCell sx={{ ...CELL, textAlign: 'right' }}>Est. Cost</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            Array.from({ length: 5 }, (_, i) => (
                                <TableRow key={i}>
                                    {[0, 1, 2, 3].map((j) => (
                                        <TableCell key={j} sx={CELL}>
                                            <Skeleton />
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))
                        ) : (
                            <>
                                <TableRow hover>
                                    <TableCell sx={{ ...CELL, fontWeight: 600 }}>AWS</TableCell>
                                    <TableCell sx={CELL}>Instances</TableCell>
                                    <TableCell sx={CELL}>
                                        <Typography variant='caption' color='text.secondary'>
                                            {instances.length} running
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={{ ...CELL, textAlign: 'right' }}>{usd(awsCost)}</TableCell>
                                </TableRow>
                                <TableRow hover>
                                    <TableCell sx={{ ...CELL, fontWeight: 600 }} rowSpan={3}>
                                        Cloudflare
                                    </TableCell>
                                    <TableCell sx={CELL}>Pages & Workers</TableCell>
                                    <TableCell sx={CELL}>
                                        <Typography variant='caption' color='text.secondary'>
                                            {workersDetail}
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={{ ...CELL, textAlign: 'right' }}>{usd(workersCost)}</TableCell>
                                </TableRow>
                                <TableRow hover>
                                    <TableCell sx={CELL}>Queues</TableCell>
                                    <TableCell sx={CELL}>
                                        <Typography variant='caption' color='text.secondary'>
                                            {queueOps.toLocaleString()} ops
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={{ ...CELL, textAlign: 'right' }}>{usd(queueCost)}</TableCell>
                                </TableRow>
                                <TableRow hover>
                                    <TableCell sx={CELL}>R2</TableCell>
                                    <TableCell sx={CELL}>
                                        <Typography variant='caption' color='text.secondary'>
                                            {(r2Bytes / 1024 ** 3).toFixed(2)} GB
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={{ ...CELL, textAlign: 'right' }}>{usd(r2Cost)}</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell sx={{ ...CELL, fontWeight: 700 }}>Total</TableCell>
                                    <TableCell sx={CELL} />
                                    <TableCell sx={CELL} />
                                    <TableCell sx={{ ...CELL, textAlign: 'right', fontWeight: 700 }}>
                                        {usd(total)}
                                    </TableCell>
                                </TableRow>
                            </>
                        )}
                    </TableBody>
                </Table>
            </Box>
        </Stack>
    );
}

export { InfraDashboard };
